// Copyright (C) 2020 Intel Corporation
//
// SPDX-License-Identifier: MIT

import React from 'react';

import { Col } from 'antd/lib/grid';
import { useTranslation } from 'react-i18next';
// eslint-disable-next-line import/no-extraneous-dependencies
import Icon from '@ant-design/icons';

import { EyesTooltip as Tooltip } from 'utils/custom-antd';

import {
    FirstIcon,
    BackJumpIcon,
    PreviousIcon,
    PlayIcon,
    PauseIcon,
    NextIcon,
    ForwardJumpIcon,
    LastIcon,
} from 'icons';

interface Props {
    playing: boolean;
    playPauseShortcut: string;
    nextFrameShortcut: string;
    previousFrameShortcut: string;
    forwardShortcut: string;
    backwardShortcut: string;
    onSwitchPlay(): void;
    onPrevFrame(): void;
    onNextFrame(): void;
    onForward(): void;
    onBackward(): void;
    onFirstFrame(): void;
    onLastFrame(): void;
}

function PlayerButtons(props: Props): JSX.Element {
    const {
        playing,
        playPauseShortcut,
        nextFrameShortcut,
        previousFrameShortcut,
        forwardShortcut,
        backwardShortcut,
        onSwitchPlay,
        onPrevFrame,
        onNextFrame,
        onForward,
        onBackward,
        onFirstFrame,
        onLastFrame,
    } = props;
    const { t } = useTranslation();

    return (
        <Col className='cvat-player-buttons'>
            <Tooltip title={t('Go to the first frame')}>
                <Icon className='cvat-player-first-button' component={FirstIcon} onClick={onFirstFrame} rev='' />
            </Tooltip>
            <Tooltip title={`${t('Go back with a step')} ${backwardShortcut}`}>
                <Icon className='cvat-player-backward-button' component={BackJumpIcon} onClick={onBackward} rev='' />
            </Tooltip>
            <Tooltip title={`${t('Go back')} ${previousFrameShortcut}`}>
                <Icon className='cvat-player-previous-button' component={PreviousIcon} onClick={onPrevFrame} rev='' />
            </Tooltip>

            {!playing ?
                (
                    <Tooltip title={`${t('Play')} ${playPauseShortcut}`}>
                        <Icon
                            className='cvat-player-play-button'
                            component={PlayIcon}
                            onClick={onSwitchPlay}
                            rev=''
                        />
                    </Tooltip>
                ) : (
                    <Tooltip className='cvat-player-pause-button' title={`${t('Pause')} ${playPauseShortcut}`}>
                        <Icon
                            component={PauseIcon}
                            onClick={onSwitchPlay}
                            rev=''
                        />
                    </Tooltip>
                )}

            <Tooltip title={`${t('Go next')} ${nextFrameShortcut}`}>
                <Icon className='cvat-player-next-button' component={NextIcon} onClick={onNextFrame} rev='' />
            </Tooltip>
            <Tooltip title={`${t('Go next with a step')} ${forwardShortcut}`}>
                <Icon className='cvat-player-forward-button' component={ForwardJumpIcon} onClick={onForward} rev='' />
            </Tooltip>
            <Tooltip title={t('Go to the last frame')}>
                <Icon className='cvat-player-last-button' component={LastIcon} onClick={onLastFrame} rev='' />
            </Tooltip>
        </Col>
    );
}

export default React.memo(PlayerButtons);
